/**
 * View types of the text check (UC-2, SC-010): one search field over every person in the
 * case, by name, nickname, date of birth, document or service number and capture tag.
 */
import type { CheckScope, NotCheckedReason, SubjectSummary } from '../types'

/** The fields a text check can search; each is a toggle chip under the search field. */
export type TextCheckField =
  | 'name'
  | 'nickname'
  | 'date_of_birth'
  | 'document_number'
  | 'service_number'
  | 'capture_tag'

export const TEXT_CHECK_FIELDS: readonly TextCheckField[] = [
  'name',
  'nickname',
  'date_of_birth',
  'document_number',
  'service_number',
  'capture_tag',
]

/**
 * Where a hit matched: a chip's field, or a finer one behind a chip — a stored name variant or
 * the name as printed (behind *name*), a national id (behind *document number*).
 */
export type TextCheckMatchField = TextCheckField | 'name_variant' | 'name_native' | 'national_id'

/**
 * How a hit matched: the text as stored, a Cyrillic–Latin transliteration variant, the start
 * of the stored value (identifiers match exact or by prefix, never fuzzy), or a near spelling.
 */
export type TextMatchKind = 'exact' | 'transliteration' | 'partial' | 'fuzzy'

/**
 * The subject of a hit: portrait, name or tag, and the last encounter. `subjectNo` holds the
 * short id; with `enrolledAt` an unnamed subject reads "Neznámy · 14:05 · 7f3a".
 */
export type TextCheckSubject = Pick<
  SubjectSummary,
  | 'id'
  | 'subjectNo'
  | 'captureTag'
  | 'givenName'
  | 'surname'
  | 'nickname'
  | 'nameNative'
  | 'sex'
  | 'portraitUrl'
  | 'latestEncounter'
> & { enrolledAt?: string | null }

export interface TextCheckHit {
  subject: TextCheckSubject
  field: TextCheckMatchField
  matchKind: TextMatchKind
  /**
   * The stored value that matched, e.g. "Tarnow" or "LX4821937", emphasised in the row. The
   * search does not return it; the row then shows the submitted text.
   */
  matchedText?: string | null
  /** A hit on an identity document: a verified holder, or only a possible one (a lead). */
  holder?: 'verified' | 'possible' | null
  /** The last encounter's `event_ref` (slice 07): the hit opens that encounter with it. */
  latestEventRef?: string | null
}

/** The committed search: the submitted text and the fields it searches. */
export interface TextCheckQuery {
  text: string
  fields: TextCheckField[]
}

/**
 * A text check: nothing typed yet, a search in flight, its hits (maybe none), or not checked —
 * the search did not run, which is never "no subject found".
 */
export type TextCheckResult =
  | { status: 'idle' }
  | { status: 'searching' }
  | {
      status: 'done'
      hits: TextCheckHit[]
      scope: Pick<CheckScope, 'caseName' | 'checkedAt' | 'audited'>
      /** Transliteration variants the search also tried, e.g. ["Tarnov", "Tarnow", "Tarnoff"]. */
      variants?: readonly string[]
    }
  | { status: 'not_checked'; reason: NotCheckedReason }

/** Everything the text check can do. */
export interface TextCheckActions {
  /**
   * The committed query changed: a field chip was toggled, or the text was submitted. Keep it
   * as the panel's `query`; the text being typed is the panel's own until then.
   */
  onQueryChange: (query: TextCheckQuery) => void
  /** Run the search; called right after `onQueryChange` on submit (every search is audited). */
  onSubmit: (query: TextCheckQuery) => void
  onOpenSubject: (subjectId: string) => void
  /** A hit: start a new encounter with that subject straight away (SC-020). */
  onNewEncounter?: (subjectId: string) => void
  /** Adding a person to an encounter (slice 06): a hit joins it, in place of "New encounter". */
  onAddToEncounter?: (subjectId: string) => void
  /** A hit's last encounter, by its `event_ref` (slice 07). */
  onOpenEncounter?: (encounterRef: string) => void
  /** No hit: enrol a new subject without a photo. */
  onEnrol: () => void
  /** No hit: check the person by a photo instead. */
  onFaceCheck: () => void
  /** Not checked: run the same search again. */
  onRetry?: () => void
}
